// Character sheet component for displaying PC stats

/**
 * @typedef {import('../../types/jsdoc-types.js').Character} Character
 * @typedef {import('../../types/jsdoc-types.js').InventoryItem} InventoryItem
 */

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Character sheet component
 * Displays character stats, inventory, and abilities
 */
class CharacterSheet extends BaseComponent {
  static properties = {
    character: { type: Object },
    isGM: { type: Boolean, attribute: 'is-gm' },
    currentUserId: { type: String, attribute: 'current-user-id' },
    _showSettings: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .character-sheet {
      color: #fff;
    }

    .character-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
      padding-top: 4px;
    }

    .character-title {
      flex: 1;
    }

    .character-name {
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 4px 0;
      color: #fff;
    }

    .character-subtitle {
      font-size: 16px;
      color: #999;
      margin: 0;
      font-weight: 400;
    }

    .settings-button,
    .gear-button {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid #555;
      border-radius: 4px;
      padding: 6px;
      color: #999;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .settings-button:hover,
    .gear-button:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: #4a9eff;
      color: #4a9eff;
    }

    .settings-button:active,
    .gear-button:active {
      transform: scale(0.95);
    }

    .section {
      margin-bottom: 16px;
    }

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    .section-title {
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #999;
      margin: 0 0 8px 0;
    }

    .section-header .section-title {
      margin: 0;
    }

    .ability-scores {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }

    .ability-score {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid #444;
      border-radius: 8px;
      padding: 8px;
      text-align: center;
    }

    .ability-label {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #999;
      margin-bottom: 4px;
    }

    .ability-value {
      font-size: 18px;
      font-weight: bold;
      color: #fff;
      line-height: 1;
      margin-bottom: 2px;
    }

    .ability-modifier {
      font-size: 12px;
      color: #888;
    }

    .hp-container,
    .ac-container {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid #444;
      border-radius: 8px;
      padding: 16px;
    }

    .hp-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 12px;
    }

    .hp-current {
      font-size: 24px;
      font-weight: bold;
      color: #fff;
    }

    .hp-separator {
      font-size: 24px;
      color: #666;
    }

    .hp-max {
      font-size: 24px;
      color: #999;
    }

    .hp-bar {
      width: 100%;
      height: 8px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      overflow: hidden;
    }

    .hp-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #ef4444 0%, #22c55e 50%, #22c55e 100%);
      transition: width 0.3s ease;
    }

    .ac-container {
      display: flex;
      justify-content: center;
    }

    .ac-value {
      font-size: 24px;
      font-weight: bold;
      color: #fff;
    }

    .inventory-simple-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .inventory-simple-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 8px;
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid #333;
      border-radius: 4px;
      font-size: 13px;
    }

    .inventory-simple-name {
      color: #ccc;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .inventory-simple-badge {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #4a9eff;
      background: rgba(74, 158, 255, 0.15);
      border: 1px solid rgba(74, 158, 255, 0.3);
      border-radius: 3px;
      padding: 2px 6px;
    }

    .item-quantity {
      color: #999;
      font-size: 12px;
      margin-left: 4px;
    }

    .burn-time {
      color: #f59e0b;
      font-size: 11px;
      margin-left: 4px;
    }

    .torch-flame-icon {
      color: #f59e0b;
      animation: flicker 0.5s ease-in-out infinite alternate;
    }

    @keyframes flicker {
      from {
        opacity: 0.8;
      }
      to {
        opacity: 1;
      }
    }

    .torch-lit {
      background: rgba(245, 158, 11, 0.08);
      border-color: rgba(245, 158, 11, 0.3);
    }

    .empty-text {
      color: #666;
      font-size: 14px;
      font-style: italic;
      text-align: center;
      padding: 16px;
    }

    .icon {
      width: 16px;
      height: 16px;
    }

    .icon-sm {
      width: 14px;
      height: 14px;
    }

  `;

  constructor() {
    super();
    /** @type {Character | null} */
    this.character = null;
    this.isGM = false;
    this.currentUserId = '';
    this._showSettings = false;
  }

  get _hpPercentage() {
    if (!this.character?.hp || !this.character?.maxHp || this.character.maxHp === 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, (this.character.hp / this.character.maxHp) * 100));
  }

  /**
   * Render death save pips and condition badges.
   * Only shown when the PC is unconscious, stable, or dead. Three success
   * pips (green), three failure pips (red). DEAD / STABLE badges replace the
   * pips when the final state has resolved.
   */
  _renderDeathSaves() {
    const conditions = this.character?.conditions || {};
    const isUnconscious = !!conditions.unconscious;
    const isStable = !!conditions.stable;
    const isDead = !!conditions.dead;

    if (!isUnconscious && !isStable && !isDead) {
      return '';
    }

    if (isDead) {
      return html`
        <div style="margin-top: 8px; padding: 6px 10px; border-radius: 4px; background: #111; color: #f87171; font-weight: bold; text-align: center; letter-spacing: 0.1em;">
          DEAD
        </div>
      `;
    }

    const successes = this.character?.deathSaveSuccesses || 0;
    const failures = this.character?.deathSaveFailures || 0;
    const successPips = [0, 1, 2].map(
      (i) => html`
        <span
          style="display:inline-block;width:12px;height:12px;border-radius:50%;margin-right:3px;background:${i < successes ? '#22c55e' : '#1f2937'};border:1px solid #22c55e;"
        ></span>
      `,
    );
    const failurePips = [0, 1, 2].map(
      (i) => html`
        <span
          style="display:inline-block;width:12px;height:12px;border-radius:50%;margin-right:3px;background:${i < failures ? '#ef4444' : '#1f2937'};border:1px solid #ef4444;"
        ></span>
      `,
    );

    return html`
      <div style="margin-top: 8px; font-size: 0.85em;">
        ${
          isStable
            ? html`
              <div style="padding: 4px 8px; border-radius: 4px; background: #14532d; color: #bbf7d0; text-align: center; font-weight: bold; letter-spacing: 0.1em;">
                STABLE
              </div>
            `
            : html`
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
                <div>
                  <span style="color: #86efac;">Successes</span> ${successPips}
                </div>
                <div>
                  <span style="color: #fca5a5;">Failures</span> ${failurePips}
                </div>
              </div>
            `
        }
      </div>
    `;
  }

  /** @param {number | undefined} score */
  _formatModifier(score) {
    if (score === undefined) return '\u2014';
    const modifier = Math.floor((score - 10) / 2);
    return modifier >= 0 ? `+${modifier}` : `${modifier}`;
  }

  /** @param {Character} character */
  _formatCharacterSubtitle(character) {
    const level = character.level ?? 1;
    const suffixes = ['st', 'nd', 'rd'];
    const levelSuffix = suffixes[((((level + 90) % 100) - 10) % 10) - 1] || 'th';
    const species = character.species || '';
    const charClass = character.class || '';
    const background = character.background ? ` (${character.background})` : '';
    return `${level}${levelSuffix} Level ${species} ${charClass}${background}`;
  }

  /** @param {string | undefined} slot */
  _formatSlotShort(slot) {
    if (!slot) return '';
    return slot === 'mainHand' ? 'MH' : 'OH';
  }

  /** @param {number | undefined} turnsRemaining */
  _formatBurnTime(turnsRemaining) {
    if (turnsRemaining === undefined || turnsRemaining <= 0) return '';
    const minutes = Math.floor(turnsRemaining / 10);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) return `${hours}h`;
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  }

  get _weaponsWithDetails() {
    if (!this.character?.inventory) return [];
    return this.character.inventory
      .map((/** @type {InventoryItem} */ item, /** @type {number} */ index) => ({ inventoryItem: item, index }))
      .filter((/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem }) => {
        const itemType = inventoryItem.type || 'weapon';
        return itemType === 'weapon';
      })
      .map((/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem, index }) => ({
        inventoryItem,
        index,
        weapon: inventoryItem,
      }));
  }

  get _itemsWithDetails() {
    if (!this.character?.inventory) return [];
    return this.character.inventory
      .map((/** @type {InventoryItem} */ item, /** @type {number} */ index) => ({ inventoryItem: item, index }))
      .filter(
        (/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem }) =>
          inventoryItem.type === 'item',
      )
      .map((/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem, index }) => ({
        inventoryItem,
        index,
        item: inventoryItem,
      }));
  }

  get _armorWithDetails() {
    if (!this.character?.inventory) return [];
    return this.character.inventory
      .map((/** @type {InventoryItem} */ item, /** @type {number} */ index) => ({ inventoryItem: item, index }))
      .filter(
        (/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem }) =>
          inventoryItem.type === 'armor' || inventoryItem.category === 'armor',
      )
      .map((/** @type {{ inventoryItem: InventoryItem, index: number }} */ { inventoryItem, index }) => ({
        inventoryItem,
        index,
        armor: inventoryItem,
      }));
  }

  get _occupiedSlots() {
    /** @type {Record<string, boolean>} */
    const slots = { mainHand: false, offHand: false, armor: false };
    if (!this.character?.inventory) return slots;

    for (const item of this.character.inventory) {
      if (item.equipped && item.slot) {
        slots[item.slot] = true;
      }
    }
    return slots;
  }

  /**
   * @param {InventoryItem} item
   * @returns {string[]}
   */
  _getAvailableSlots(item) {
    if (!item.equippableSlots) return [];
    return item.equippableSlots.filter((/** @type {string} */ slot) => !this._occupiedSlots[slot]);
  }

  /** @param {string | undefined} slot */
  _formatSlot(slot) {
    if (!slot) return '';
    if (slot === 'mainHand') return 'Main Hand';
    if (slot === 'offHand') return 'Off Hand';
    if (slot === 'armor') return 'Armor';
    return slot;
  }

  _renderSettingsIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
        />
        <circle cx="12" cy="12" r="3" />
      </svg>
    `;
  }

  _renderPackageOpenIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 22v-9" />
        <path
          d="M15.17 2.21a1.67 1.67 0 0 1 1.63 0L21 4.57a1.93 1.93 0 0 1 0 3.36L8.82 14.79a1.655 1.655 0 0 1-1.64 0L3 12.43a1.93 1.93 0 0 1 0-3.36z"
        />
        <path
          d="M20 13v3.87a2.06 2.06 0 0 1-1.11 1.83l-6 3.08a1.93 1.93 0 0 1-1.78 0l-6-3.08A2.06 2.06 0 0 1 4 16.87V13"
        />
        <path
          d="M21 12.43a1.93 1.93 0 0 0 0-3.36L8.83 2.2a1.64 1.64 0 0 0-1.63 0L3 4.57a1.93 1.93 0 0 0 0 3.36l12.18 6.86a1.636 1.636 0 0 0 1.63 0z"
        />
      </svg>
    `;
  }

  _renderFlameIcon() {
    return html`
      <svg
        class="icon-sm torch-flame-icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"
        />
      </svg>
    `;
  }

  _renderCloseIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    `;
  }

  render() {
    if (!this.character) {
      return html`<div class="character-sheet"><div class="empty-text">No character selected</div></div>`;
    }

    const abilities =
      this.character.abilityScores || /** @type {import('../../types/jsdoc-types.js').AbilityScores} */ ({});

    return html`
      <div class="character-sheet">
        <div class="character-header">
          <div class="character-title">
            <h2 class="character-name">${this.character.name}</h2>
            <p class="character-subtitle">${this._formatCharacterSubtitle(this.character)}</p>
          </div>
          ${
            this.isGM
              ? html`
                <button class="settings-button" title="PC Settings" @click="${() => (this._showSettings = true)}">
                  ${this._renderSettingsIcon()}
                </button>
              `
              : ''
          }
        </div>

        <!-- Ability Scores -->
        <div class="section">
          <h3 class="section-title">Ability Scores</h3>
          <div class="ability-scores">
            <div class="ability-score">
              <div class="ability-label">STR</div>
              <div class="ability-value">${abilities.str ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.str)}</div>
            </div>
            <div class="ability-score">
              <div class="ability-label">DEX</div>
              <div class="ability-value">${abilities.dex ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.dex)}</div>
            </div>
            <div class="ability-score">
              <div class="ability-label">CON</div>
              <div class="ability-value">${abilities.con ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.con)}</div>
            </div>
            <div class="ability-score">
              <div class="ability-label">INT</div>
              <div class="ability-value">${abilities.int ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.int)}</div>
            </div>
            <div class="ability-score">
              <div class="ability-label">WIS</div>
              <div class="ability-value">${abilities.wis ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.wis)}</div>
            </div>
            <div class="ability-score">
              <div class="ability-label">CHA</div>
              <div class="ability-value">${abilities.cha ?? '\u2014'}</div>
              <div class="ability-modifier">${this._formatModifier(abilities.cha)}</div>
            </div>
          </div>
        </div>

        <!-- Hit Points -->
        <div class="section">
          <h3 class="section-title">Hit Points</h3>
          <div class="hp-container">
            <div class="hp-display">
              <span class="hp-current">${this.character.hp ?? '\u2014'}</span>
              <span class="hp-separator">/</span>
              <span class="hp-max">${this.character.maxHp ?? '\u2014'}</span>
            </div>
            ${
              this.character.hp !== undefined && this.character.maxHp !== undefined
                ? html`
                  <div class="hp-bar">
                    <div class="hp-bar-fill" style="width: ${this._hpPercentage}%"></div>
                  </div>
                `
                : ''
            }
            ${this._renderDeathSaves()}
          </div>
        </div>

        <!-- Armor Class -->
        <div class="section">
          <h3 class="section-title">Armor Class</h3>
          <div class="ac-container">
            <span class="ac-value">${this.character.ac ?? '\u2014'}</span>
          </div>
        </div>

        <!-- Inventory -->
        <div class="section">
          <div class="section-header">
            <h3 class="section-title">Inventory</h3>
            <button
              class="gear-button"
              title="Manage Gear"
              @click="${() =>
                this.dispatchEvent(new CustomEvent('open-equipment-screen', { bubbles: true, composed: true }))}"
            >
              ${this._renderPackageOpenIcon()}
            </button>
          </div>
          ${
            this._weaponsWithDetails.length === 0 && this._itemsWithDetails.length === 0
              ? html` <div class="empty-text">No items</div> `
              : html`
                <div class="inventory-simple-list">
                  <!-- Weapons -->
                  ${this._weaponsWithDetails.map(
                    (item) => html`
                      <div class="inventory-simple-item">
                        <span class="inventory-simple-name"> ${item.weapon?.name || 'Unknown'} </span>
                        ${
                          item.inventoryItem.equipped
                            ? html`
                              <span class="inventory-simple-badge">
                                ${this._formatSlotShort(item.inventoryItem.slot)}
                              </span>
                            `
                            : ''
                        }
                      </div>
                    `,
                  )}
                  <!-- Items (Torches, etc.) -->
                  ${this._itemsWithDetails.map(
                    (item) => html`
                      <div class="inventory-simple-item ${item.inventoryItem.isLit ? 'torch-lit' : ''}">
                        <span class="inventory-simple-name">
                          ${item.inventoryItem.isLit ? this._renderFlameIcon() : ''}
                          ${item.inventoryItem.name || 'Unknown'}
                          ${
                            (item.inventoryItem.quantity || 0) > 1
                              ? html` <span class="item-quantity">×${item.inventoryItem.quantity}</span> `
                              : ''
                          }
                          ${
                            item.inventoryItem.isLit
                              ? html`
                                <span class="burn-time"
                                  >(${this._formatBurnTime(item.inventoryItem.burnsRemainingTurns)})</span
                                >
                              `
                              : ''
                          }
                        </span>
                        ${
                          item.inventoryItem.equipped
                            ? html`
                              <span class="inventory-simple-badge">
                                ${this._formatSlotShort(item.inventoryItem.slot)}
                              </span>
                            `
                            : ''
                        }
                      </div>
                    `,
                  )}
                </div>
              `
          }
        </div>
      </div>
    `;
  }
}

customElements.define('character-sheet', CharacterSheet);
